// =============================================================================
//  Copyright : 2020 semantics GmbH
//  Date      : $Date: 2024-05-24 19:32:15 +0200 (Fri, 24 May 2024) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 143070 $
//  Purpose   : statistic parts
// =============================================================================
// ---Third-party imports
import React from 'react'
import {Icon, Table} from 'semantic-ui-react'

// ---Framework imports
import {Actions, Sel} from 'uix/registries'
import log from 'uix/logging'
import {keys, values, mapItems, dict, isNil, isFunction, flatten} from 'ldash'
import {useSelector, useState, usePartContext, useApi, useSummary, useTimestats} from 'uix/hooks'
import {
  HeaderSegment,
  SplitPane,
  XMenu,
  InView,
  TranslatedTable,
  ResizableDialog,
  PortalTarget,
} from 'uix/components'
import {formatSI, prettyBytes} from 'uix/utils'
import {DomainLabel} from 'uix/domain/label'
import {TypeDescription, DatatypeDescription, TreeNode} from 'uix/types/components'
import {StateDescription} from 'uix/states/components'
import {useStatusTypes} from 'uix/states/hooks'
import {interval2granularity} from 'uix/utils/date'
import {MinorBottomTabGroupSegment} from 'uix/tree/tabs'

// ---Local imports
import {Plot, formatDateWithGranularity} from './plots'
import {DomainTreeStats} from './components'
import {TIMESTATS_COLSPEC, FILTER_TIMESTATS_ROWS} from './timestats'

// ---Exports
const get_i18n_row = (itypes, i18n, summaryName, row) => {
  if (summaryName == 'datatype') {
    return i18n.get(`types_datatype_${itypes.datatypeMap[row]}`)
  }
  return `${summaryName}_${row}`
}
const SummaryRowCell0 = ({name, value, other}) => {
  /* first column in summary stats table
  value: cell value
  */
  let itypes = useSelector(Sel.itypes)
  let {data: domains} = useSelector(Sel.domains)
  let {i18n} = usePartContext()
  if (name == 'domain') {
    return <DomainLabel odid={value} />
  }
  if (name == 'structure' || itypes.datatypeMapInverse[name]) {
    let s = name == 'structure' ? 'substructure' : name
    let dt = itypes.datatypeMapInverse[s]
    return <TypeDescription datatype={dt} subtype={value} />
  }

  if (name == 'datatype') {
    return <DatatypeDescription datatype={value} />
  }

  if (name == 'archive_state') {
    return i18n.span(`archivestate_${value}`)
  }
  if (name == 'archive_store') {
    return <span>{other.archivestore}</span>
  }
  if (name == 'metadata_origin') {
    return i18n.span(`xmlorigin_${value}`)
  }

  if (name.endsWith('state')) {
    if (value == '') return 'Leer'
    return (
      <StateDescription
        statusType={name.slice(0, -5)}
        statusValue={value}
        icon={{width: 16, verticalAlign: 'middle'}}
      />
    )
  }
  return (
    <>
      <Icon name="folder outline" /> {get_i18n_row(itypes, i18n, name, value)}
    </>
  )
}

const render_value = (value, value_type, fmtSi) => {
  if (isNil(value)) {
    return ''
  }
  if (value_type == 'filesize') {
    return prettyBytes(value)
  } else {
    return fmtSi ? formatSI(value, 1) : value.toLocaleString()
  }
}

const NavList = ({name, row, col}) => {
  let {data} = useApi('getStatsNavlist', {name: name + '_summary', row, col})
  let nodes = data?.nodes || []
  let tnodes = nodes.map(n => (
    <div key={n.otid}>
      <TreeNode node={n} link linkLazy />
    </div>
  ))
  return <div>{tnodes}</div>
}
const navlistLink = ({name, rowname, colname, rendered_value}) => {
  let [open, setOpen] = useState(false)
  return (
    <>
      {open && (
        <ResizableDialog open onClose={() => setOpen(false)}>
          <NavList name={name} row={rowname} col={colname} />{' '}
        </ResizableDialog>
      )}
      <span style={{cursor: 'pointer'}} onClick={() => setOpen(!open)}>
        {rendered_value}
      </span>
    </>
  )
}

let get_i18n_head = (i18n, colname) => {
  return i18n.get(colname)
}
const SummaryTable = ({name}) => {
  let [fmtSi, setFmtSi] = useState(true)
  let {dname, i18n} = usePartContext()
  let {sumdata, columns, navlistCols, loading, reload} = useSummary(name, dname)
  let {filled: cols, types: coltypes} = columns

  let menuPortal = `summary_${name}`
  let menu = (
    <XMenu>
      <XMenu.Item icon={{name: 'refresh', loading}} onClick={reload} />
      <XMenu.Toggle content="SI" value={fmtSi} set={setFmtSi} />
      <PortalTarget id={menuPortal} />
    </XMenu>
  )
  let cell_renderers = dict(
    navlistCols.map(colname => [
      colname,
      (colname, rowname, rendered_value) => navlistLink({name, colname, rowname, rendered_value}),
    ])
  )
  let tdata = mapItems(sumdata, (row, data) => ({rowtype: row, ...data}))
  let tcols = cols.map((colname, i) => ({
    id: colname,
    accessor: colname,
    Header: get_i18n_head(i18n, colname),
    Cell: cell_renderers[colname]
      ? ({value, original}) =>
          cell_renderers[colname](
            colname,
            original.rowtype,
            render_value(value, coltypes[colname], fmtSi)
          )
      : ({value}) => render_value(value, coltypes[colname], fmtSi),
    selectable: true,
    autoselect: i < 8,
  }))
  tcols = [
    {
      id: 'rowtype',
      accessor: 'rowtype',
      Header: 'Type',
      Cell: ({value, original}) => <SummaryRowCell0 name={name} value={value} other={original} />,
    },
    ...tcols,
  ]

  // persistence versioning (persistKey) depends on columns
  // so we must not render empty cols and reset persisted data
  let colsKnown = tcols.length > 1

  return (
    <HeaderSegment key={name} basic header={i18n.span(`${name}_summary`)} menu={menu}>
      {colsKnown && (
        <TranslatedTable
          compact
          data={tdata}
          columns={tcols}
          menuPortal={menuPortal}
          persistKey={`summary_${name}`}
        />
      )}
    </HeaderSegment>
  )
}

const TimestatsTable = ({name}) => {
  let [startDate, setStartDate] = useState('')
  let [fmtSi, setFmtSi] = useState(true)
  let [interval, setInterval] = useState('month')
  let {dname, i18n, dconfig} = usePartContext()
  let iconfig = useSelector(Sel.iconfig)
  let [tstype, datefield] = name.split('_')

  let {tsdata, dt_start, loading, reload} = useTimestats({tstype, datefield, interval, startDate})

  let menu = (
    <XMenu>
      <XMenu.Item icon={{name: 'refresh', loading}} onClick={reload} />
      <XMenu.Toggle content="SI" value={fmtSi} set={setFmtSi} />
      <XMenu.DateInput
        value={dt_start?.split('T')[0] || ''}
        onValueChange={setStartDate}
        debounce={500}
      />
      <XMenu.Dropdown
        options={['day', 'week', 'month', 'quart'].map(v => ({
          value: v,
          key: v,
          text: i18n.span(`timestats_interval_${v}`),
        }))}
        value={interval}
        onChange={(e, data) => setInterval(data.value)}
      />
    </XMenu>
  )

  let cols = TIMESTATS_COLSPEC[tstype] ?? TIMESTATS_COLSPEC.default
  if (isFunction(cols.at(-1))) {
    let other_cols = cols.at(-1)({iconfig, dconfig, tsdata})
    cols = [...cols.slice(0, -1), ...other_cols]
  }

  let headers = cols.map(([colname, coli18n]) => (
    <Table.HeaderCell key={colname}>{i18n.span(coli18n)}</Table.HeaderCell>
  ))

  let filter_rows = FILTER_TIMESTATS_ROWS[name]
  if (filter_rows) {
    tsdata = tsdata.filter(filter_rows)
  }

  let rows = tsdata.map(dtrow => {
    let cells = cols.map(([colname, coli18n]) => (
      <Table.Cell key={colname} collapsing>
        {render_value(dtrow[colname], null, fmtSi)}
      </Table.Cell>
    ))
    return (
      <Table.Row key={dtrow.datetime}>
        <Table.Cell collapsing style={{width: '15%'}}>
          {formatDateWithGranularity(new Date(dtrow.datetime), interval2granularity[interval])}
        </Table.Cell>
        {cells}
      </Table.Row>
    )
  })
  return (
    <HeaderSegment key={name} basic header={i18n.span(`timestats_${name}`)} menu={menu}>
      <Table celled striped compact="very">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>{i18n.span('date')}</Table.HeaderCell>
            {headers}
          </Table.Row>
        </Table.Header>
        <Table.Body>{rows}</Table.Body>
      </Table>
    </HeaderSegment>
  )
}

export const StatsTables = ({dname, go, request, i18n}) => {
  // for triggering config load
  useStatusTypes()

  let {data} = useApi('filter/getStatsGroupingInfo', {}, {cached: true, autoreset: false})
  let _groups = data?.groups || []
  let groups = dict(_groups.map(g => [g.name, g]))
  let selectedGroup = request.id || 'overview'

  let menu = (
    <XMenu>
      <XMenu.Dropdown
        options={keys(groups).map(v => ({
          value: v,
          key: v,
          text: i18n.get(`qastatsgroup_${v}`),
        }))}
        value={selectedGroup}
        onChange={(e, data) => go({id: data.value})}
      />
    </XMenu>
  )
  let content = null
  if (groups[selectedGroup]) {
    content = groups[selectedGroup].items
      .map(e => {
        if (e.type == 'summary') {
          return <SummaryTable key={e.name} name={e.name.replace('_summary', '')} />
        } else if (e.type == 'timestats') {
          return <TimestatsTable key={e.name} name={e.name} />
        }
      })
      .filter(e => e)
  }
  return (
    <TabGroupStats icon="bar chart" header="Details" menu={menu}>
      {content}
    </TabGroupStats>
  )
}

export const StatsPlots = ({}) => {
  let plots = keys(Plot.PLOTS).map(name => (
    <InView key={name}>
      <Plot flex showMenu showLegend name={name} />
    </InView>
  ))
  return (
    <TabGroupStats header="Plots" icon="chart bar" flexInner={false}>
      <div style={{height: `${plots.length * 300}px`}}>
        <SplitPane flex flexInner borderless>
          {plots}
        </SplitPane>
      </div>
    </TabGroupStats>
  )
}

export const StatsDomainTable = ({}) => {
  let subdomains = useSelector(Sel.subdomains)
  return (
    <TabGroupStats headerless fitted>
      <DomainTreeStats menuPortal="StatsTabGroupPortal" />
    </TabGroupStats>
  )
}

const TabGroupStats = ({children, menu, icon, header, subheader, flexInner = true, ...props}) => {
  return (
    <MinorBottomTabGroupSegment
      header={header}
      subheader={subheader}
      flexInner={flexInner}
      overflowInner
      icon={icon}
      menu={menu}
      style={{marginBottom: '0px', paddingBottom: '0px'}}
      portal="StatsTabGroupPortal"
      {...props}
    >
      {children}
    </MinorBottomTabGroupSegment>
  )
}
